import { Component, DestroyRef, inject, OnInit, Output, ViewChild, EventEmitter } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppInsightsService } from '../../../appInsights/app-insights.service';
import { RnErrorHandlerService } from '../../../services/rn-error-handler/rn-error-handler.service';
import { LicenseService } from '../../../services/rnapi2-service/apis/api';
import {
  RnLicenseBaseVM,
  RnLicenseFullVM,
  RnLicenseGetCatalog,
  RnLicensesCreate
} from '../../../services/rnapi2-service/models/models';
import { LicenseAddEditConfig } from './license-add-edit-config';
import { ToasterService, ToastType } from '@rn-platform/frontend-shared-data-access-toaster';
import { ModalComponent } from '@rn-platform/frontend-shared-ui-common';
import { LicenseAddEditModalModalService } from './license-add-edit-modal.service';
import { ModalState } from 'src/app/shared/services/base-modal/base-modal.service';

@Component({
  selector: 'app-license-add-edit',
  templateUrl: './license-add-edit.component.html',
  styleUrls: ['./license-add-edit.component.scss'],
  standalone: false
})
export class LicenseAddEditComponent implements OnInit {
  private readonly licenseService = inject(LicenseService);
  private readonly appInsights = inject(AppInsightsService);
  private readonly toasterService = inject(ToasterService);
  private readonly errorHandler = inject(RnErrorHandlerService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.licenseForm = this.formBuilder.group({
      licenseYear: ['', Validators.required],
      licenseEdition: ['', Validators.required],
      licenseCountry: ['US', Validators.required],
      licenseKey: ['', Validators.required],
      productNumber: ['', Validators.required]
    });
  }

  private licenseCatalog: RnLicenseBaseVM[] = [];
  licenseConfig: LicenseAddEditConfig;
  private readonly licenseAddEditModalModalService = inject(LicenseAddEditModalModalService);
  public actionText: string;
  public licenseKeyMask = '0000-0000-0000-000';
  public productNumberMask = '000-000';
  licenseForm: FormGroup;
  yearsSelect = [];
  countries: string[] = [];
  editionsSelect = [];
  isAutoAssignable = true;
  loading = false;
  processing = false;
  hasSubmitted = false;
  @ViewChild('formTop') formTop;
  @ViewChild('licenseKeyText') licenseKeyText;
  @ViewChild('modalRef') modalComponent: ModalComponent;
  @Output() confirmAddLicense = new EventEmitter<void>();

  get licenseYear() {
    return this.licenseForm?.get('licenseYear');
  }

  get licenseEdition() {
    return this.licenseForm?.get('licenseEdition');
  }

  get licenseCountry() {
    return this.licenseForm?.get('licenseCountry');
  }

  get licenseKey() {
    return this.licenseForm?.get('licenseKey');
  }

  get productNumber() {
    return this.licenseForm?.get('productNumber');
  }

  ngOnInit(): void {
    this.subscribeModalService();
  }

  subscribeModalService(): void {
    this.licenseAddEditModalModalService.modalState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: ModalState<LicenseAddEditConfig>) => {
      if (!data?.config) {
        return;
      }

      this.processing = false;
      this.loading = false;
      this.hasSubmitted = false;
      this.licenseForm = this.formBuilder.group({
        licenseYear: ['', Validators.required],
        licenseEdition: ['', Validators.required],
        licenseCountry: ['US', Validators.required],
        licenseKey: ['', Validators.required],
        productNumber: ['', Validators.required]
      });
      this.licenseForm.markAsPristine();
      this.licenseForm.markAsUntouched();
      this.licenseConfig = data.config;
      this.actionText = this.licenseConfig.IsEdit ? 'Edit' : 'Add';
      this.loading = true;
      const payload = new RnLicenseGetCatalog();
      payload.IncludeByol = true;
      payload.OrganizationId = this.licenseConfig.OrganizationID;
      this.licenseService.apiV2LicensesGetlicenseitemsPost(payload, 'response').subscribe((r) => {
        this.licenseCatalog = r.body.data.filter((l) => {
          return !l.Type.includes('Annual');
        });
        const years = [];
        this.licenseCatalog.forEach((l) => {
          if (!years.includes(l.Version)) {
            years.push(l.Version);
          }
        });

        this.yearsSelect = this.createDropdownOptions(
          years.sort((a, b) => {
            return b.localeCompare(a);
          })
        );
        this.loading = false;
      });

      if (data?.isOpen) {
        this.open();
      } else {
        this.close();
      }
    });
  }

  open(): void {
    this.modalComponent?.open();
  }

  close(): void {
    this.modalComponent?.close();
  }

  versionChange() {
    this.countries = [];
    this.licenseCatalog
      .filter((l) => {
        return l.Version === this.licenseYear.value;
      })
      .forEach((l) => {
        if (!this.countries.includes(l.Country)) {
          this.countries.push(l.Country);
        }
      });

    if (!this.countries.includes(this.licenseCountry.value)) {
      this.licenseCountry.setValue('');
    }

    this.countryChange();
  }

  countryChange() {
    const editions = [];
    this.licenseCatalog.forEach((l) => {
      if (l.Version === this.licenseYear.value && l.Country === this.licenseCountry.value) {
        if (!editions.includes(l.Edition)) {
          editions.push(l.Edition);
        }
      }
    });

    editions.sort((a, b) => {
      if (a < b) {
        return -1;
      } else if (a > b) {
        return 1;
      }
      return 0;
    });
    if (!editions.includes(this.licenseEdition.value)) {
      this.licenseEdition.setValue('');
    }
    this.editionsSelect = this.createDropdownOptions(editions);
  }

  private createDropdownOptions(options) {
    const selectOptions = options.map((o) => {
      return {
        label: o,
        value: o
      };
    });
    return selectOptions;
  }

  scrollToInvalidField() {
    if (!this.licenseYear.valid || !this.licenseEdition.valid) {
      this.formTop?.nativeElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        container: 'nearest'
      });
    } else {
      this.licenseKeyText?.nativeElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        container: 'nearest'
      });
    }
  }

  isFormInvalid() {
    return (
      !!this.licenseYear.errors ||
      !!this.licenseEdition.errors ||
      !!this.licenseCountry.errors ||
      !!this.licenseKey.errors ||
      !!this.productNumber.errors
    );
  }

  validateAndSubmit() {
    if (this.isFormInvalid()) {
      this.hasSubmitted = true;
      this.scrollToInvalidField();
      return;
    }
    this.processing = true;
    const matches = this.licenseCatalog.filter((l) => {
      return (
        l.Version === this.licenseYear.value &&
        l.Country === this.licenseCountry.value &&
        l.Edition === this.licenseEdition.value &&
        l.LicenseTypeName.toUpperCase() === 'BYOL'
      );
    });
    const itemFlavor = matches[0];

    if (this.licenseConfig.IsEdit) {
      //Pending Edit
    } else {
      const createPayload = new RnLicensesCreate();
      createPayload.AffectedOrganizationId = this.licenseConfig.OrganizationID;
      createPayload.FlavorCountry = this.licenseCountry.value;
      createPayload.OrganizationID = this.licenseConfig.OrganizationID;
      createPayload.LicenseTypeName = 'BYOL';
      createPayload.FlavorID = itemFlavor.FlavorId;
      createPayload.ItemID = itemFlavor.ItemId;
      createPayload.isFSP = false;
      createPayload.LicenseData = new RnLicenseFullVM();
      createPayload.LicenseData.LicenseCount = -1;
      createPayload.LicenseData.License1 = this.licenseKey.value.replace(/-/g, '') || 'NA';
      createPayload.LicenseData.License2 = this.productNumber.value.replace(/-/g, '') || 'NA';
      createPayload.LicenseData.IsAutoAssignable = this.isAutoAssignable;
      createPayload.LicenseData.IsProAdvisor = false;
      createPayload.LicenseData.Version = this.licenseYear.value;
      createPayload.LicenseData.Country = this.licenseCountry.value;
      createPayload.LicenseData.Edition = this.licenseEdition.value;
      createPayload.LicenseData.Flavor = '';
      createPayload.LicenseData.ItemId = itemFlavor.ItemId;
      createPayload.LicenseData.TypeId = itemFlavor.TypeId;
      this.licenseService.apiV2LicensesCreatePost(createPayload, 'response').subscribe((r) => {
        if (r && r.body && r.body.Success) {
          this.appInsights.logEvent('Add license - Added');
          const message = 'License added successfully';
          const id = 'apphub-license-add-edit-add-success-toast';
          this.toasterService.addToast(ToastType.Success_, message, message, id, id);
          this.confirmAddLicense.emit();
          this.close();
        } else {
          const msgs = r && r.body ? r.body.Messages : null;
          this.errorHandler.handleError('There was a problem adding the license.', msgs);
          const message = `There was a problem adding the license. Please contact support ${this.licenseConfig.OrgWLPSupportWebsite} for further assistance.`;
          const id = 'apphub-license-add-edit-add-success-toast';
          this.toasterService.addToast(ToastType.Error_, message, message, id, id);
        }
      });
    }
  }
}
